import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Eye } from "lucide-react";
import { Button } from "react-bootstrap";
import { saveAs } from "file-saver";
import { RiShareForwardLine, RiDownloadFill } from "react-icons/ri";
import { MdOutlineBlock, MdOutlineDeleteOutline } from "react-icons/md";
import { HiOutlineMail } from "react-icons/hi";
import { FaArrowLeft } from "react-icons/fa6";
import { SiTicktick } from "react-icons/si";
import { ImConfused } from "react-icons/im";
import { Document, Packer, Paragraph, TextRun } from "docx";
import "../../Student/StuStyles/StuLiveRegister.css";
import ApiService from "../../../nhrc_apis/ApiService";

function UstTotalApplicationsRes() {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const { jobId } = useParams();
  const location = useLocation();

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [skillFilter, setSkillFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const pageSize = 10;
  const navigate = useNavigate();

  const [jobTitle, setJobTitle] = useState("Unknown Position");

  useEffect(() => {
    if (location.state && location.state.jobTitle) {
      setJobTitle(location.state.jobTitle);
    }
  }, [location.state]);

  // ━━━━ FETCH REGISTRATIONS FROM API ━━━━
  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("━━━━ Fetching registrations for training ID:", jobId, "━━━━");

      const response = await ApiService.TrainingAPI.getTrainingRegistrations(jobId);

      console.log("REGISTRATIONS FULL RESPONSE :", response);
      console.log("REGISTRATIONS DATA          :", response?.data);

      const data = response?.data;

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.registrations)
        ? data.registrations
        : [];

      console.log("PARSED REGISTRATIONS LIST :", list);

      const mapped = list.map((item) => ({
        id:          item.id || item.registration_id,
        name:        item.full_name   || "—",
        email:       item.email       || "—",
        phone:       item.phone       || "—",
        nhrcid:      item.nhrc_id     || "—",
        memberType:  item.member_type || "—",
        location:    item.location    || "—",
        iama:        item.iam_a       || "—",
        collegeName: item.college_name     || "—",
        yearPassout: item.year_of_passout  || "—",
        companyName: item.company_name     || "—",
        companyLoc:  item.company_location || "—",
        status:      item.status      || "PENDING",
      }));

      console.log("MAPPED CANDIDATES :", mapped);
      setCandidates(mapped);

    } catch (err) {
      console.error("━━━━ FETCH REGISTRATIONS ERROR ━━━━");
      console.error("ERROR        :", err);
      console.error("ERROR MESSAGE:", err?.message);
      console.error("ERROR STATUS :", err?.response?.status);
      console.error("ERROR DATA   :", err?.response?.data);
      setError("Failed to load registrations. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) fetchRegistrations();
  }, [fetchRegistrations, jobId]);

  // ━━━━ COUNTS ━━━━
  const totalCount    = candidates.length;
  const studentCount  = candidates.filter((c) => c.memberType === "STUDENT").length;
  const employeeCount = candidates.filter((c) => c.memberType === "HR" || c.memberType === "EMPLOYEE").length;

  // ━━━━ APPROVE SELECTED ━━━━
  const handleApprove = async () => {
    if (selectedIds.length === 0) {
      alert("Please select candidate(s) to approve");
      return;
    }
    if (!window.confirm(`Approve ${selectedIds.length} registration(s)?`)) return;

    setActionLoading(true);
    try {
      await Promise.allSettled(
        selectedIds.map((id) =>
          ApiService.RegistrationAPI.approveTrainingRegistration(id)
        )
      );
      console.log("APPROVE SUCCESS for IDs:", selectedIds);
      // Update status locally
      setCandidates((prev) =>
        prev.map((c) =>
          selectedIds.includes(c.id) ? { ...c, status: "APPROVED" } : c
        )
      );
      setSelectedIds([]);
      alert("Selected registrations approved successfully.");
    } catch (err) {
      console.error("APPROVE ERROR:", err);
      alert("Failed to approve some registrations. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // ━━━━ REJECT SELECTED ━━━━
  const handleReject = async () => {
    if (selectedIds.length === 0) {
      alert("Please select candidate(s) to reject");
      return;
    }
    if (!window.confirm(`Reject ${selectedIds.length} registration(s)?`)) return;

    setActionLoading(true);
    try {
      await Promise.allSettled(
        selectedIds.map((id) =>
          ApiService.RegistrationAPI.rejectTrainingRegistration(id)
        )
      );
      console.log("REJECT SUCCESS for IDs:", selectedIds);
      // Update status locally
      setCandidates((prev) =>
        prev.map((c) =>
          selectedIds.includes(c.id) ? { ...c, status: "REJECTED" } : c
        )
      );
      setSelectedIds([]);
      alert("Selected registrations rejected successfully.");
    } catch (err) {
      console.error("REJECT ERROR:", err);
      alert("Failed to reject some registrations. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // ━━━━ DELETE SELECTED ━━━━
  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      alert("Please select candidate(s) to delete");
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} candidate(s)?`)) return;

    setActionLoading(true);
    try {
      await Promise.allSettled(
        selectedIds.map((id) =>
          ApiService.RegistrationAPI.deleteTrainingRegistration(id)
        )
      );
      console.log("DELETE SUCCESS for IDs:", selectedIds);
      setCandidates((prev) => prev.filter((c) => !selectedIds.includes(c.id)));
      setSelectedIds([]);
      setPage(1);
    } catch (err) {
      console.error("DELETE ERROR:", err);
      alert("Failed to delete some registrations. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAll = useMemo(
    () =>
      candidates.filter((c) => {
        if (statusFilter !== "all" && c.status.toLowerCase() !== statusFilter.toLowerCase())
          return false;
        if (nameFilter && !c.name.toLowerCase().includes(nameFilter.toLowerCase()))
          return false;
        if (skillFilter && !c.nhrcid.toLowerCase().includes(skillFilter.toLowerCase()))
          return false;
        if (emailFilter && !c.email.toLowerCase().includes(emailFilter.toLowerCase()))
          return false;
        return true;
      }),
    [candidates, statusFilter, nameFilter, skillFilter, emailFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredAll.length / pageSize));

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAll.slice(start, start + pageSize);
  }, [filteredAll, page]);

  const allSelected = paged.length > 0 && paged.every((c) => selectedIds.includes(c.id));

  const toggleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(paged.map((c) => c.id));
    else setSelectedIds([]);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved" || s === "registered") return "border border-primary text-success bg-transparent";
    if (s === "rejected") return "border border-danger text-danger bg-transparent";
    return "border border-secondary text-secondary bg-transparent";
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setNameFilter("");
    setSkillFilter("");
    setEmailFilter("");
    setPage(1);
  };

  const handleViewCandidate = (id) => {
    const candidate = candidates.find((c) => c.id === id);
    if (!candidate) return;
    setSelectedCandidate(candidate);
    setShowPopup(true);
  };

  const handleBackToJobs = () => {
    navigate(-1);
  };

  const handleDownloadSelected = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one candidate to download.");
      return;
    }

    const selectedCandidates = candidates.filter((c) => selectedIds.includes(c.id));

    if (selectedCandidates.length === 0) {
      alert("No matching candidates found.");
      return;
    }

    const doc = new Document({
      sections: selectedCandidates.map((c) => ({
        properties: {},
        children: [
          new Paragraph({
            children: [
              new TextRun({ text: "Candidate Details", bold: true, size: 28 }),
            ],
          }),
          new Paragraph(""),
          new Paragraph(`ID: ${c.id}`),
          new Paragraph(`Name: ${c.name}`),
          new Paragraph(`Email: ${c.email}`),
          new Paragraph(`Phone: ${c.phone}`),
          new Paragraph(`NHRC ID: ${c.nhrcid}`),
          new Paragraph(`Member Type: ${c.memberType}`),
          new Paragraph(`Location: ${c.location}`),
          new Paragraph(`I am a: ${c.iama}`),
          new Paragraph(`College Name: ${c.collegeName}`),
          new Paragraph(`Year of Passout: ${c.yearPassout}`),
          new Paragraph(`Company Name: ${c.companyName}`),
          new Paragraph(`Company Location: ${c.companyLoc}`),
          new Paragraph(`Status: ${c.status}`),
          new Paragraph(""),
          new Paragraph("------------------------------------------------------"),
          new Paragraph(""),
        ],
      })),
    });

    try {
      const blob = await Packer.toBlob(doc);
      const fileName = `candidates_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download candidates.");
    }
  };

  // ━━━━ LOADING STATE ━━━━
  if (loading) {
    return (
      <div className="container-fluid py-3 mt-5">
        <h3 className="training-title mt-2 fw-bold" style={{ color: "#8D0000" }}>
          UPDATE SKILLS & TRAINING
        </h3>
        <div className="text-center py-5">
          <div className="spinner-border text-danger" role="status" />
          <p className="mt-3 text-muted">Loading registrations...</p>
        </div>
      </div>
    );
  }

  // ━━━━ ERROR STATE ━━━━
  if (error) {
    return (
      <div className="container-fluid py-3 mt-5">
        <h3 className="training-title mt-2 fw-bold" style={{ color: "#8D0000" }}>
          UPDATE SKILLS & TRAINING
        </h3>
        <div className="text-center py-5">
          <p className="text-danger fw-semibold">{error}</p>
          <button className="btn btn-outline-danger mt-2" onClick={fetchRegistrations}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 mt-5">
      <h3 className="training-title mt-2 fw-bold" style={{ color: "#8D0000" }}>
        UPDATE SKILLS & TRAINING
      </h3>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="justify-content-start">
          <button
            className="btn btn-outline-secondary btn-sm mb-2"
            onClick={handleBackToJobs}
          >
            <FaArrowLeft size={18} /> Back to Jobs
          </button>
          <h5 className="mb-0">Update Skills And Trainings Responses</h5>
          <small className="text-muted">
            Position: <span className="fw-semibold">{jobTitle}</span>
          </small>
        </div>
        <div
          md={6}
          className="d-flex justify-content-md-end justify-content-start mt-2 mt-md-0"
        >
          <div className="btn-toolbar" role="toolbar">
            <div className="btn-group btn-group-sm" role="group">

              {/* ✅ APPROVE — calls API */}
              <Button
                variant="success"
                size="sm"
                className="me-2 border rounded-3"
                disabled={actionLoading}
                onClick={handleApprove}
              >
                <SiTicktick /> Approve
              </Button>

              {/* ✅ REJECT — calls API */}
              <Button
                variant="outline-danger"
                size="sm"
                className="me-2 border rounded-3"
                disabled={actionLoading}
                onClick={handleReject}
              >
                <ImConfused /> Reject
              </Button>

              <Button variant="primary" size="sm" className="me-2 border rounded-3">
                <HiOutlineMail /> Email
              </Button>
              <Button variant="secondary" size="sm" className="me-2 border rounded-3">
                <RiShareForwardLine /> Forward
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2 border rounded-3"
                onClick={handleDownloadSelected}
              >
                <RiDownloadFill /> Download
              </Button>

              {/* ✅ DELETE — calls API */}
              <Button
                variant="outline-danger"
                size="sm"
                className="border rounded-3"
                disabled={actionLoading}
                onClick={handleDelete}
              >
                <MdOutlineDeleteOutline /> Delete
              </Button>

            </div>
          </div>
        </div>
      </div>

      {/* ✅ REGISTRATION COUNT SUMMARY CARDS */}
      <div className="d-flex flex-wrap gap-3 mb-4">
        <div className="card border-0 shadow-sm px-4 py-3 text-center">
          <span className="fw-bold fs-4 text-dark">{totalCount}</span>
          <span className="text-muted small">Total Registrations</span>
        </div>
        <div className="card border-0 shadow-sm px-4 py-3 text-center">
          <span className="fw-bold fs-4 text-primary">{studentCount}</span>
          <span className="text-muted small">Students</span>
        </div>
        <div className="card border-0 shadow-sm px-4 py-3 text-center">
          <span className="fw-bold fs-4 text-success">{employeeCount}</span>
          <span className="text-muted small">Employees / HR</span>
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-light border rounded-3 p-3 mb-3">
        <div className="row g-3 align-items-end">
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="all">All</option>
              <option value="registered">Registered</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Candidate Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name"
              value={nameFilter}
              onChange={(e) => { setNameFilter(e.target.value); setPage(1); }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              placeholder="Search by email"
              value={emailFilter}
              onChange={(e) => { setEmailFilter(e.target.value); setPage(1); }}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">NHRC ID</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by nhrcid"
              value={skillFilter}
              onChange={(e) => { setSkillFilter(e.target.value); setPage(1); }}
            />
          </div>
          <div className="col-md-3 d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* TABLE HEADER */}
      <div className="border-bottom pb-2 mb-2 d-flex align-items-center">
        <div className="form-check me-3">
          <input
            type="checkbox"
            style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
            className="form-check-input border-3"
            checked={allSelected}
            onChange={toggleSelectAll}
          />
        </div>
        <div className="flex-grow-1 d-flex text-muted small fw-semibold">
          <div className="col-2 text-start">Name</div>
          <div className="col-2 text-start">Email</div>
          <div className="col-2 text-start">Phone</div>
          <div className="col-2 text-start">NHRC ID</div>
          <div className="col-2">Status</div>
          <div className="col-2 text-center">Actions</div>
        </div>
      </div>

      {/* TABLE ROWS */}
      {paged.map((c) => (
        <div key={c.id} className="border-bottom py-2 d-flex align-items-center">
          <div className="form-check me-3">
            <input
              type="checkbox"
              style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
              className="form-check-input border-3"
              checked={selectedIds.includes(c.id)}
              onChange={() => toggleSelect(c.id)}
            />
          </div>
          <div className="flex-grow-1 d-flex align-items-center">
            <div className="col-2 text-start">
              <div className="fw-semibold">{c.name}</div>
            </div>
            <div className="col-2 text-start">
              <small className="text-break">{c.email}</small>
            </div>
            <div className="col-2 text-start">
              <small>{c.phone}</small>
            </div>
            <div className="col-2 text-start">
              <small>{c.nhrcid}</small>
            </div>
            <div className="col-2">
              <span
                className={`badge ${getStatusBadgeClass(c.status)} d-inline-flex align-items-center justify-content-center`}
                style={{
                  fontSize: "0.85rem",
                  minWidth: "120px",
                  padding: "0.35rem 0.5rem",
                  cursor: "default",
                }}
              >
                {c.status}
              </span>
            </div>
            <div className="col-2 text-center">
              <button
                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                title="View details"
                onClick={() => handleViewCandidate(c.id)}
              >
                <Eye size={16} />
                <span className="d-none d-md-inline">View</span>
              </button>
            </div>
          </div>
        </div>
      ))}

      {paged.length === 0 && (
        <p className="text-muted mt-3">No candidates match the filters.</p>
      )}

      {/* PAGINATION */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing {filteredAll.length === 0 ? 0 : (page - 1) * pageSize + 1}-
          {Math.min(page * pageSize, filteredAll.length)} of {filteredAll.length} responses
        </small>
        <div className="btn-group">
          <button
            className="btn btn-sm border rounded-2 m-2 btn-primary"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <small className="text-muted m-3">
            Page {page} of {Math.max(1, totalPages)}
          </small>
          <button
            className="btn btn-sm border m-2 rounded-2 btn-primary"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {/* === POPUP START === */}
      <div className={`popup-form-overlay ${showPopup ? "show" : ""}`}>
        <div className="popup-form">

          <div className="popup-header">
            <h3>Candidate View</h3>
            <button className="close-btn-res" onClick={() => setShowPopup(false)}>
              &times;
            </button>
          </div>

          <form className="stu-live-pg-register-card">
            <div className="stu-live-pg-register-grid">

              <div className="stu-live-pg-register-field">
                <label>Full Name*</label>
                <input readOnly value={selectedCandidate?.name || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Mobile Number*</label>
                <input readOnly value={selectedCandidate?.phone || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Email Id*</label>
                <input type="email" readOnly value={selectedCandidate?.email || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Location*</label>
                <input readOnly value={selectedCandidate?.location || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>I am a*</label>
                <input readOnly value={selectedCandidate?.iama || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>NHRC ID*</label>
                <input readOnly value={selectedCandidate?.nhrcid || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>College Name*</label>
                <input readOnly value={selectedCandidate?.collegeName || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Year of Passout*</label>
                <input readOnly value={selectedCandidate?.yearPassout || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Company Name*</label>
                <input readOnly value={selectedCandidate?.companyName || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Company Location*</label>
                <input readOnly value={selectedCandidate?.companyLoc || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Member Type*</label>
                <input readOnly value={selectedCandidate?.memberType || ""} />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Status*</label>
                <input readOnly value={selectedCandidate?.status || ""} />
              </div>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default UstTotalApplicationsRes;